import { useRef, useMemo, useCallback, useState, useEffect } from "react";

export default function OrgChartView({
  divisions,
  departments,
  teams,
  employees,
  divisionColors,
  searchText,
  setSearchText,
  executiveList = [],
  getDictionaryValue,
  getDictionaryValueObject,
  divisionColorVars,
  getOrgChartColors,
}) {
  const gridRef = useRef();
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  const [focusedEmployeeId, setFocusedEmployeeId] = useState(null);
  const employeeRefs = useRef({});
  const divisionRefs = useRef({});
  const departmentRefs = useRef({});
  const teamRefs = useRef({});
  const [searchResults, setSearchResults] = useState([]);
  const [focusedResultIdx, setFocusedResultIdx] = useState(0);
  const searchInputRef = useRef();
  const [showExecDropdown, setShowExecDropdown] = useState(false);

  // Memoized sorted data
  const sortedDivisions = useMemo(
    () =>
      [...divisions]
        .filter((d) => !d.isArchived)
        .sort((a, b) => {
          const aId = a.TWDivisionId;
          const bId = b.TWDivisionId;
          if (aId == null || aId === "") return 1;
          if (bId == null || bId === "") return -1;
          if (!isNaN(aId) && !isNaN(bId)) return Number(aId) - Number(bId);
          return aId?.toString().localeCompare(bId?.toString());
        }),
    [divisions]
  );

  const sortByTWId = useCallback(
    (arr, twKey) =>
      [...arr]
        .filter((item) => !item.isArchived)
        .sort((a, b) => {
          const aId = a[twKey];
          const bId = b[twKey];
          if (aId == null || aId === "") return 1;
          if (bId == null || bId === "") return -1;
          if (!isNaN(aId) && !isNaN(bId)) return Number(aId) - Number(bId);
          return aId?.toString().localeCompare(bId?.toString());
        }),
    []
  );

  const getEmployeeHierarchyLevel = useCallback(
    (emp) => {
      if (!emp._position) return 9999;
      const posObj = getDictionaryValueObject("Positions", emp._position);
      if (posObj && posObj.extraData && typeof posObj.extraData.hierarchyLevel === "number") {
        return posObj.extraData.hierarchyLevel;
      }
      return 9999;
    },
    [getDictionaryValueObject]
  );

  const getTeamsForDepartment = useCallback(
    (departmentId) => teams.filter((team) => team._department === departmentId && !team.isArchived),
    [teams]
  );
  const getDepartmentsForDivision = useCallback(
    (divisionId) => departments.filter((dept) => dept._division === divisionId && !dept.isArchived),
    [departments]
  );
  const getEmployeesForTeam = useCallback(
    (teamId) => employees.filter((emp) => emp._team === teamId && emp._team !== null),
    [employees]
  );

  // Scroll arrows logic
  const checkArrows = useCallback(() => {
    if (gridRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = gridRef.current;
      setShowLeftArrow(scrollLeft > 10);
      setShowRightArrow(scrollLeft + clientWidth < scrollWidth - 10);
    }
  }, []);

  const scrollGrid = useCallback(
    (direction) => {
      if (gridRef.current) {
        const gridWidth = gridRef.current.clientWidth;
        const amount = Math.floor(gridWidth * 0.85);
        gridRef.current.scrollBy({ left: direction === "right" ? amount : -amount, behavior: "smooth" });
        setTimeout(checkArrows, 400);
      }
    },
    [checkArrows]
  );

  useEffect(() => {
    const gridEl = gridRef.current;
    if (gridEl) {
      gridEl.addEventListener("scroll", checkArrows);
      checkArrows();
      return () => gridEl.removeEventListener("scroll", checkArrows);
    }
  }, [checkArrows]);

  // Search logic
  useEffect(() => {
    if (!searchText.trim()) {
      setFocusedEmployeeId(null);
      setSearchResults([]);
      setFocusedResultIdx(0);
      return;
    }
    const search = searchText.trim().toLowerCase();
    const results = [];

    for (const emp of employees) {
      if (emp._team === null) continue;
      const fullName = `${emp.firstName} ${emp.lastName}`.toLowerCase();
      const reverseFullName = `${emp.lastName} ${emp.firstName}`.toLowerCase();
      const hrCode = emp.hrCode ? emp.hrCode.toLowerCase() : "";
      if (
        emp.firstName.toLowerCase().includes(search) ||
        emp.lastName.toLowerCase().includes(search) ||
        fullName.includes(search) ||
        reverseFullName.includes(search) ||
        hrCode.includes(search)
      ) {
        results.push({ type: "employee", id: emp.id });
      }
    }
    for (const div of divisions) {
      if (div.name.toLowerCase().includes(search)) {
        results.push({ type: "division", id: div.id });
      }
    }
    for (const dept of departments) {
      if (dept.name.toLowerCase().includes(search)) {
        results.push({ type: "department", id: dept.id });
      }
    }
    for (const team of teams) {
      if (team.name.toLowerCase().includes(search)) {
        results.push({ type: "team", id: team.id });
      }
    }

    setSearchResults(results);
    setFocusedResultIdx(0);

    if (results.length > 0) {
      focusAndScrollToResult(results[0]);
    } else {
      setFocusedEmployeeId(null);
    }
  }, [searchText, employees, divisions, departments, teams]);

  function focusAndScrollToResult(result) {
    if (!result) return;
    if (result.type === "employee") {
      setFocusedEmployeeId(result.id);
      setTimeout(() => {
        if (employeeRefs.current[result.id]) {
          employeeRefs.current[result.id].scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 100);
    } else if (result.type === "division") {
      setTimeout(() => {
        if (divisionRefs.current[result.id]) {
          divisionRefs.current[result.id].scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 100);
    } else if (result.type === "department") {
      setTimeout(() => {
        if (departmentRefs.current[result.id]) {
          departmentRefs.current[result.id].scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 100);
    } else if (result.type === "team") {
      setTimeout(() => {
        if (teamRefs.current[result.id]) {
          teamRefs.current[result.id].scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 100);
    }
  }

  const handleNextResult = useCallback(() => {
    if (searchResults.length === 0) return;
    const nextIdx = (focusedResultIdx + 1) % searchResults.length;
    setFocusedResultIdx(nextIdx);
    focusAndScrollToResult(searchResults[nextIdx]);
  }, [searchResults, focusedResultIdx]);

  const handlePrevResult = useCallback(() => {
    if (searchResults.length === 0) return;
    const prevIdx = (focusedResultIdx - 1 + searchResults.length) % searchResults.length;
    setFocusedResultIdx(prevIdx);
    focusAndScrollToResult(searchResults[prevIdx]);
  }, [searchResults, focusedResultIdx]);

  useEffect(() => {
    function onKeyDown(e) {
      if (document.activeElement !== searchInputRef.current || searchResults.length === 0) return;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        handleNextResult();
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        handlePrevResult();
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [searchResults, focusedResultIdx, handleNextResult, handlePrevResult]);

  useEffect(() => {
    if (!showExecDropdown) return;
    function handleClickOutside(event) {
      const dropdown = document.querySelector(".orgchart-executive-dropdown-container");
      if (dropdown && !dropdown.contains(event.target)) {
        setShowExecDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showExecDropdown]);

  function isNameFocused(name) {
    if (!searchText.trim()) return false;
    const search = searchText.trim().toLowerCase();
    return name.toLowerCase().includes(search);
  }

  return (
    <div className="page-wrapper orgchart-wrapper">
      <div className="orgchart-executive-dropdown-container">
        <div
          className={`orgchart-executive-dropdown-btn${showExecDropdown ? " open" : ""}`}
          onClick={() => setShowExecDropdown((v) => !v)}
        >
          Executive Leadership
          <span className="material-icons">{showExecDropdown ? "expand_less" : "expand_more"}</span>
        </div>
        {showExecDropdown && (
          <div className="orgchart-executive-dropdown-list">
            <div className="divider"></div>
            {executiveList.length === 0 ? (
              <div className="orgchart-executive-empty">No executive members</div>
            ) : (
              (() => {
                const maxColumns = 5;
                const n = executiveList.length;
                let columns = Math.min(maxColumns, n);
                let rows = Math.ceil(n / columns);

                if (n > maxColumns * rows) {
                  columns = Math.ceil(Math.sqrt(n));
                  rows = Math.ceil(n / columns);
                }

                const cols = Array.from({ length: columns }, (_, colIdx) =>
                  Array.from({ length: rows }, (_, rowIdx) => {
                    const idx = rowIdx + colIdx * rows;
                    return idx < n ? executiveList[idx] : null;
                  }).filter(Boolean)
                );

                return (
                  <div className="orgchart-executive-grid">
                    {cols.map((col, colIdx) => (
                      <div key={colIdx} style={{ display: "flex", flexDirection: "column", minWidth: 180 }}>
                        {col.map((emp) =>
                          emp ? (
                            <div key={emp.hrCode} className="orgchart-employee">
                              <span className="user-badge orgchart-employee-avatar">
                                {emp.firstName?.[0] || ""}
                                {emp.lastName?.[0] || ""}
                              </span>
                              <span className="orgchart-employee-details">
                                <span className="orgchart-employee-name">
                                  {getDictionaryValue("Positions", emp._position) || "No position"}
                                </span>
                                <span className="orgchart-employee-position">
                                  {emp.firstName} {emp.lastName}
                                </span>
                              </span>
                            </div>
                          ) : null
                        )}
                      </div>
                    ))}
                  </div>
                );
              })()
            )}
          </div>
        )}
      </div>
      <div className="orgchart-searchbar-fixed">
        <div className="searchbar-container">
          <input
            ref={searchInputRef}
            type="text"
            className="searchbar-input"
            placeholder="Search"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
          <div className="searchbar-result-nav">
            <button onClick={handlePrevResult} disabled={searchResults.length === 0} tabIndex={-1}>
              <i className="material-icons">chevron_left</i>
            </button>
            <span>{searchResults.length > 0 ? `${focusedResultIdx + 1} / ${searchResults.length}` : "0 / 0"}</span>
            <button onClick={handleNextResult} disabled={searchResults.length === 0} tabIndex={-1}>
              <i className="material-icons">chevron_right</i>
            </button>
          </div>
          <span className="searchbar-icon">
            <i className="material-icons">search</i>
          </span>
        </div>
      </div>
      <div className="orgchart-grid" ref={gridRef}>
        {sortedDivisions.length === 0 ? (
          <div className="orgchart-empty-search">No results found</div>
        ) : (
          sortedDivisions.map((division) => {
            const baseHex = divisionColors[division.id] || divisionColorVars[0].hex;
            const isFirst = String(division.TWDivisionId) === "0";
            let managerObj = null;
            if (division._divisionManager) {
              managerObj = employees?.find((e) => e.hrCode === division._divisionManager);
            }
            return (
              <div key={division.id} className="orgchart-division-column">
                {!isFirst && <div className="orgchart-division-fake-tile" />}
                <div
                  className={`orgchart-employee orgchart-division-tile orgchart-division-manager-tile${isNameFocused(division.name) ? " focused" : ""}`}
                  ref={(el) => (divisionRefs.current[division.id] = el)}
                >
                  <div className="orgchart-division-manager-topbar" style={{ backgroundColor: baseHex }} />
                  <div className="orgchart-division-manager-content">
                    <div className="orgchart-division-name">
                      Division {division.TWDivisionId || "#"} - {division.name}
                    </div>
                    {managerObj ? (
                      <div className="orgchart-division-manager-label">
                        <span className="user-badge orgchart-employee-avatar" style={{ backgroundColor: baseHex }}>
                          {managerObj.firstName[0]}
                          {managerObj.lastName[0]}
                        </span>
                        <span className="orgchart-employee-details">
                          <span className="orgchart-employee-name">
                            {managerObj.firstName} {managerObj.lastName}
                          </span>
                          <span className="orgchart-employee-position">
                            {managerObj.hrCode
                              ? `${managerObj.hrCode} | ${getDictionaryValue("Positions", managerObj._position) || "No position"}`
                              : getDictionaryValue("Positions", managerObj._position) || "No position"}
                          </span>
                        </span>
                      </div>
                    ) : (
                      <div className="orgchart-division-manager-label">
                        <span className="user-badge orgchart-employee-avatar" style={{ backgroundColor: baseHex }}>
                          N/A
                        </span>
                        <span className="orgchart-employee-details empty-manager-details">
                          <span className="orgchart-employee-name" style={{ color: "#bbb" }}>
                            No manager assigned
                          </span>
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="orgchart-department-row">
                  {sortByTWId(getDepartmentsForDivision(division.mosId), "TWDepartmentId").map((department, deptIdx) => {
                    const colors = getOrgChartColors(baseHex, deptIdx);
                    const hasManagers = Array.isArray(department._departmentManager) && department._departmentManager.length > 0;
                    return (
                      <div key={department.id} className="orgchart-department-block">
                        <div
                          className={`orgchart-department-tile${isNameFocused(department.name) ? " focused" : ""}${!hasManagers ? " no-manager" : ""}`}
                          style={{ backgroundColor: colors.department, padding: 0 }}
                          ref={(el) => (departmentRefs.current[department.id] = el)}
                        >
                          <div className={`orgchart-department-header${!hasManagers ? " rounded-bottom" : ""}`}>
                            Department {department.TWDepartmentId || "#"} - {department.name}
                          </div>
                          {hasManagers && (
                            <div className="orgchart-department-manager-area">
                              {department._departmentManager.map((managerHrCode) => {
                                const managerObj = employees?.find((e) => e.hrCode === managerHrCode);
                                if (!managerObj) return null;
                                return (
                                  <div className="orgchart-division-manager-label" key={managerHrCode}>
                                    <span
                                      className="user-badge orgchart-employee-avatar"
                                      style={{ backgroundColor: colors.department }}
                                    >
                                      {managerObj.firstName[0]}
                                      {managerObj.lastName[0]}
                                    </span>
                                    <span className="orgchart-employee-details">
                                      <span className="orgchart-employee-name">
                                        {managerObj.firstName} {managerObj.lastName}
                                      </span>
                                      <span className="orgchart-employee-position">
                                        {managerObj.hrCode
                                          ? `${managerObj.hrCode} | ${getDictionaryValue("Positions", managerObj._position) || "No position"}`
                                          : getDictionaryValue("Positions", managerObj._position) || "No position"}
                                      </span>
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                        <div className="orgchart-team-row-scrollable">
                          {(() => {
                            const teams = sortByTWId(getTeamsForDepartment(department.mosId), "TWTeamId");
                            const grouped = {};
                            teams.forEach((team) => {
                              const match = String(team.TWTeamId).match(/^(\d+)/);
                              if (match) {
                                const prefix = match[1];
                                if (!grouped[prefix]) grouped[prefix] = [];
                                grouped[prefix].push(team);
                              } else {
                                grouped[team.TWTeamId] = [team];
                              }
                            });

                            return Object.entries(grouped).map(([prefix, teamsWithPrefix]) => {
                              const isGrouped = /^\d+$/.test(prefix) && teamsWithPrefix.length > 1;
                              if (isGrouped) {
                                return (
                                  <div key={prefix} className="orgchart-team-tile-wrapper">
                                    <div
                                      className={
                                        "orgchart-team-tile" +
                                        (teamsWithPrefix.some((team) => isNameFocused(team.name)) ? " focused" : "")
                                      }
                                      ref={(el) => {
                                        teamsWithPrefix.forEach((team) => {
                                          teamRefs.current[team.id] = el;
                                        });
                                      }}
                                    >
                                      {teamsWithPrefix.map((team) => {
                                        const teamEmployees = getEmployeesForTeam(team.mosId)
                                          .slice()
                                          .sort((a, b) => getEmployeeHierarchyLevel(a) - getEmployeeHierarchyLevel(b));
                                        return (
                                          <div key={team.id}>
                                            <div className="orgchart-team-title">
                                              Team {team.TWTeamId || "#"} - {team.name}
                                            </div>
                                            <div className="orgchart-employee-list">
                                              {teamEmployees.length === 0 ? (
                                                <div className="orgchart-team-empty">No people assigned</div>
                                              ) : (
                                                teamEmployees.map((emp) => (
                                                  <div
                                                    key={emp.id}
                                                    className={`orgchart-employee${emp.id === focusedEmployeeId ? " focused" : ""}`}
                                                    ref={(el) => (employeeRefs.current[emp.id] = el)}
                                                  >
                                                    <span
                                                      className="user-badge orgchart-employee-avatar"
                                                      style={{ backgroundColor: colors.department }}
                                                    >
                                                      {emp.firstName[0]}
                                                      {emp.lastName[0]}
                                                    </span>
                                                    <span className="orgchart-employee-details">
                                                      <span className="orgchart-employee-name">
                                                        {emp.firstName} {emp.lastName}
                                                      </span>
                                                      <span className="orgchart-employee-position">
                                                        {emp.hrCode
                                                          ? `${emp.hrCode} | ${getDictionaryValue("Positions", emp._position) || "No position"}`
                                                          : getDictionaryValue("Positions", emp._position) || "No position"}
                                                      </span>
                                                    </span>
                                                  </div>
                                                ))
                                              )}
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                );
                              } else {
                                return teamsWithPrefix.map((team) => {
                                  const teamEmployees = getEmployeesForTeam(team.mosId)
                                    .slice()
                                    .sort((a, b) => getEmployeeHierarchyLevel(a) - getEmployeeHierarchyLevel(b));
                                  return (
                                    <div key={team.id} className="orgchart-team-tile-wrapper">
                                      <div
                                        className={`orgchart-team-tile${isNameFocused(team.name) ? " focused" : ""}`}
                                        ref={(el) => (teamRefs.current[team.id] = el)}
                                      >
                                        <div className="orgchart-team-title">
                                          Team {team.TWTeamId || "#"} - {team.name}
                                        </div>
                                        <div className="orgchart-employee-list">
                                          {teamEmployees.length === 0 ? (
                                            <div className="orgchart-team-empty">No people assigned</div>
                                          ) : (
                                            teamEmployees.map((emp) => (
                                              <div
                                                key={emp.id}
                                                className={`orgchart-employee${emp.id === focusedEmployeeId ? " focused" : ""}`}
                                                ref={(el) => (employeeRefs.current[emp.id] = el)}
                                              >
                                                <span
                                                  className="user-badge orgchart-employee-avatar"
                                                  style={{ backgroundColor: colors.department }}
                                                >
                                                  {emp.firstName[0]}
                                                  {emp.lastName[0]}
                                                </span>
                                                <span className="orgchart-employee-details">
                                                  <span className="orgchart-employee-name">
                                                    {emp.firstName} {emp.lastName}
                                                  </span>
                                                  <span className="orgchart-employee-position">
                                                    {emp.hrCode
                                                      ? `${emp.hrCode} | ${getDictionaryValue("Positions", emp._position) || "No position"}`
                                                      : getDictionaryValue("Positions", emp._position) || "No position"}
                                                  </span>
                                                </span>
                                              </div>
                                            ))
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                });
                              }
                            });
                          })()}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })
        )}
      </div>
      {showLeftArrow && (
        <button className="orgchart-arrow orgchart-arrow-left" onClick={() => scrollGrid("left")}>
          <span className="material-icons">chevron_left</span>
        </button>
      )}
      {showRightArrow && (
        <button className="orgchart-arrow orgchart-arrow-right" onClick={() => scrollGrid("right")}>
          <span className="material-icons">chevron_right</span>
        </button>
      )}
    </div>
  );
}
